import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Typography,
    Alert,
    Snackbar,
    MenuItem,
    CircularProgress,
    Chip,
    Box,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { apiService } from '../../services/api';
import { CreateMerchantRequest, MerchantStatus } from '../../types';
import { getEnvironmentConfig } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FormContainer,
    FormTitle,
    FormSection,
    SectionTitle,
    FormRow,
    FormField,
    FullWidthField,
    ButtonContainer,
    SubmitButton,
    ResetButton,
} from './MerchantForm.styles';
import { useForm, Controller } from 'react-hook-form';

interface MerchantFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

const MerchantForm: React.FC<MerchantFormProps> = ({ onSuccess, onCancel }) => {
    const { id } = useParams<{ id?: string }>();
    const [merchantData, setMerchantData] =
        useState<CreateMerchantRequest | null>(null);
    const navigate = useNavigate();
    const config = getEnvironmentConfig();
    const endpoint = `${config.API_BASE_URL}/merchant/create`;
    const isEditMode = !!id;

    const formatDate = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const parseDate = (dateString: string): Date | null => {
        if (!dateString) return null;
        const [day, month, year] = dateString.split('/').map(Number);
        return new Date(year, month - 1, day);
    };

    // Initialize with empty form data
    const initialFormData: CreateMerchantRequest = {
        name: '',
        dba: '',
        url: '',
        caprate: null,
        capacity: null,
        address: '',
        city: '',
        state: '',
        zipCode: '',
        ein: null,
        mpa: '',
        persona: '',
        status: MerchantStatus.ACTIVE,
        email: '',
        startDate: '',
        mpaStartDate: '',
        merchantCode: '',
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [fileUploading, setFileUploading] = useState(false);
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CreateMerchantRequest>({
        mode: 'onChange',
        defaultValues: {
            name: '',
            dba: '',
            url: '',
            caprate: null,
            capacity: null,
            address: '',
            city: '',
            state: '',
            zipCode: '',
            ein: null,
            mpa: '',
            persona: '',
            status: MerchantStatus.ACTIVE,
            email: '',
            startDate: '',
            mpaStartDate: '',
            merchantCode: '',
        },
    });

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            if (!id) return;

            if (!cancelled) {
                setLoading(true);
                setError(null);
            }

            try {
                // Fetch merchant data
                const merchantResponse = await apiService.getMerchantById(
                    Number(id)
                );
                if (merchantResponse.data?.length > 0) {
                    const merchantData = merchantResponse.data[0];
                    console.log('hi', merchantData);
                    if (!cancelled) {
                        const formattedData: CreateMerchantRequest = {
                            name: merchantData.name || '',
                            dba: merchantData.dba || '',
                            url: merchantData.url || '',
                            caprate: merchantData.caprate ?? null,
                            capacity: merchantData.capacity ?? null,
                            address: merchantData.address || '',
                            city: merchantData.city || '',
                            state: merchantData.state || '',
                            zipCode: merchantData.zipCode || '',
                            ein: merchantData.ein ?? null,
                            email: merchantData.email || '',
                            merchantCode: merchantData.merchantCode || '',
                            status:
                                merchantData.status?.toUpperCase() ===
                                'INACTIVE'
                                    ? MerchantStatus.INACTIVE
                                    : MerchantStatus.ACTIVE,

                            // ✅ Dates must be in string dd/MM/yyyy format
                            startDate: merchantData.startDate || '',
                            mpaStartDate: merchantData.mpaStartDate || '',

                            // ✅ File paths come as strings
                            mpa: merchantData.mpa || '',
                            persona: merchantData.persona || '',
                        };

                        setMerchantData(formattedData);
                        reset(formattedData);
                        // setMerchantData(merchantData);
                        // reset(merchantData);
                    }
                } else {
                    if (!cancelled) setError('No merchant data found');
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                if (!cancelled)
                    setError('Unexpected error occurred while fetching data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [id]);

    // Set initial dates on mount
    useEffect(() => {
        const today = new Date();
    }, []);

    const onSubmit = async (data: CreateMerchantRequest) => {
        setLoading(true);
        setError(null);
        try {
            let response;
            if (id) {
                // Edit API
                response = await apiService.updateMerchant(Number(id), data);
                console.log('edit data', data);
            } else {
                //Create API
                response = await apiService.createMerchant(data);
            }
            if (response.success) {
                setSuccess(
                    id
                        ? 'Merchant updated successfully!'
                        : 'Merchant created successfully!'
                );
                reset(); // Reset form
                onSuccess?.();
                setTimeout(() => {
                    navigate('/merchants');
                }, 2000);
            } else {
                setError(response.message || 'Something went wrong');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        reset();
    };
    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };
    const uploadFileAndReturnPath = async (file: File): Promise<string> => {
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await fetch(
                'http://localhost:4000/file-upload/upload',
                {
                    method: 'POST',
                    body: uploadData,
                }
            );

            if (!response.ok) {
                throw new Error('File upload failed');
            }

            const result = await response.json();
            return result.filePath;
        } catch (err) {
            setError('Failed to upload file. Please try again.');
            console.error(err);
            return '';
        } finally {
            setFileUploading(false);
        }
    };

    const getShrinkProps = (value: any) => ({
        InputLabelProps: { shrink: !!value },
    });

    const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Only allow digits
        if (!/^\d*$/.test(value)) {
            e.preventDefault();
            return;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormContainer elevation={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormTitle>
                        <Typography variant="h4">
                            {id ? 'Edit Merchant' : 'Create New Merchant'}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                justifyContent: 'center',
                                mt: 2,
                            }}
                        ></Box>
                    </FormTitle>

                    {/* Basic Information Section */}
                    <FormSection>
                        <SectionTitle>
                            <Typography variant="h6">
                                Basic Information
                            </Typography>
                        </SectionTitle>
                        <FormRow>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: 'Name is required',
                                    minLength: {
                                        value: 2,
                                        message: 'Minimum 2 characters',
                                    },
                                    maxLength: {
                                        value: 20,
                                        message: 'Maximum 20 characters',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Merchant Name"
                                        fullWidth
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="dba"
                                control={control}
                                rules={{ required: 'DBA is required' }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="DBA (Doing Business As)"
                                        fullWidth
                                        error={!!errors.dba}
                                        helperText={errors.dba?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                        <FormRow>
                            <Controller
                                name="url"
                                control={control}
                                rules={{
                                    required: 'URL is required',
                                    pattern: {
                                        value: /^(https?:\/\/)?([\w\-])+\.\w{2,}.*$/,
                                        message: 'Enter a valid URL',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Website URL"
                                        fullWidth
                                        error={!!errors.url}
                                        helperText={errors.url?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                    </FormSection>

                    {/* Business Metrics Section */}
                    <FormSection>
                        <SectionTitle>
                            <Typography variant="h6">
                                Business Metrics
                            </Typography>
                        </SectionTitle>
                        <FormRow>
                            <Controller
                                name="caprate"
                                control={control}
                                rules={{
                                    required: 'Caprate is required',
                                    min: {
                                        value: 0,
                                        message: 'Value must be greater than 0',
                                    },
                                    max: {
                                        value: 1000000,
                                        message: 'Too large',
                                    },
                                    pattern: {
                                        value: /^[0-9]+(\.[0-9]+)?$/,
                                        message: 'Only numbers are allowed',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Volume"
                                        type="text"
                                        onInput={handleNumberInput}
                                        placeholder="0"
                                        fullWidth
                                        error={!!errors.caprate}
                                        helperText={errors.caprate?.message}
                                        InputLabelProps={{
                                            shrink: Boolean(
                                                field.value ?? field.value === 0
                                            ),
                                        }}
                                    />
                                )}
                            />

                            <Controller
                                name="capacity"
                                control={control}
                                rules={{
                                    required: 'Approved Volume is required',
                                    pattern: {
                                        value: /^[0-9]+(\.[0-9]+)?$/,
                                        message: 'Only numbers are allowed',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Approved Volume"
                                        type="text"
                                        onInput={handleNumberInput}
                                        placeholder="0"
                                        fullWidth
                                        error={!!errors.capacity}
                                        helperText={errors.capacity?.message}
                                        InputLabelProps={{
                                            shrink: Boolean(
                                                field.value ?? field.value === 0
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                        <FormRow>
                            <Controller
                                name="ein"
                                control={control}
                                rules={{
                                    required: 'EIN is required',
                                    pattern: {
                                        value: /^\d{9}$/, // Exactly 9 digits
                                        message:
                                            'EIN must be exactly 9 digits with numbers only',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="EIN"
                                        type="text"
                                        onInput={handleNumberInput}
                                        placeholder="0"
                                        fullWidth
                                        error={!!errors.ein}
                                        helperText={errors.ein?.message}
                                        InputLabelProps={{
                                            shrink: Boolean(
                                                field.value ?? field.value === 0
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="merchantCode"
                                control={control}
                                rules={{
                                    required: 'Merchant Code is required',
                                    minLength: {
                                        value: 3,
                                        message: 'Min 3 characters',
                                    },
                                    maxLength: {
                                        value: 10,
                                        message: 'Max 10 characters',
                                    },
                                    pattern: {
                                        value: /^[A-Za-z0-9_-]+$/,
                                        message:
                                            'Only alphanumeric, dash, and underscore allowed',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Merchant Code"
                                        fullWidth
                                        error={!!errors.merchantCode}
                                        helperText={
                                            errors.merchantCode?.message
                                        }
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                        <FormRow>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Enter a valid email',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="status"
                                control={control}
                                rules={{ required: 'Status is required' }}
                                render={({ field }) => (
                                    <FormField
                                        label="Status"
                                        select
                                        fullWidth
                                        {...field} // field.value and field.onChange are bound
                                        error={!!errors.status}
                                        helperText={errors.status?.message}
                                    >
                                        {Object.values(MerchantStatus).map(
                                            status => (
                                                <MenuItem
                                                    key={status}
                                                    value={status}
                                                >
                                                    {status
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        status
                                                            .slice(1)
                                                            .toLowerCase()}
                                                </MenuItem>
                                            )
                                        )}
                                    </FormField>
                                )}
                            />
                        </FormRow>
                        <FormRow>
                            <Controller
                                name="startDate"
                                control={control}
                                rules={{ required: 'Start Date is required' }}
                                render={({ field }) => (
                                    <DatePicker
                                        label="Start Date"
                                        {...field}
                                        value={
                                            field.value
                                                ? parseDate(field.value)
                                                : null
                                        }
                                        onChange={date => {
                                            const jsDate =
                                                date instanceof Date
                                                    ? date
                                                    : date?.toDate?.();
                                            const formatted = jsDate
                                                ? formatDate(jsDate)
                                                : '';
                                            field.onChange(formatted);
                                        }}
                                        format="dd/MM/yyyy"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.startDate,
                                                helperText:
                                                    errors.startDate?.message ||
                                                    '',
                                                sx: { marginTop: '8px' },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="mpaStartDate"
                                control={control}
                                rules={{
                                    required: 'MPA Start Date is required',
                                }}
                                render={({ field }) => (
                                    <DatePicker
                                        label="MPA Start Date"
                                        {...field}
                                        value={
                                            field.value
                                                ? parseDate(field.value)
                                                : null
                                        }
                                        onChange={date => {
                                            const jsDate =
                                                date instanceof Date
                                                    ? date
                                                    : date?.toDate?.();
                                            const formatted = jsDate
                                                ? formatDate(jsDate)
                                                : '';
                                            field.onChange(formatted);
                                        }}
                                        format="dd/MM/yyyy"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.mpaStartDate,
                                                helperText:
                                                    errors.mpaStartDate
                                                        ?.message || '',
                                                sx: { marginTop: '8px' },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                    </FormSection>

                    {/* Address Section */}
                    <FormSection>
                        <SectionTitle>
                            <Typography variant="h6">
                                Address Information
                            </Typography>
                        </SectionTitle>
                        {/* <FullWidthField
                            label="Address"
                            {...register('address', {
                                required: 'Address is required',
                                minLength: {
                                    value: 5,
                                    message: 'Address is too short',
                                },
                                maxLength: {
                                    value: 200,
                                    message: 'Address is too long',
                                },
                            })}
                            error={!!errors.address}
                            helperText={errors.address?.message}
                            multiline
                            rows={2}
                            InputLabelProps={{ shrink: isEditMode }}
                        /> */}
                        <Controller
                            name="address"
                            control={control}
                            rules={{
                                required: 'Address is required',
                                minLength: {
                                    value: 5,
                                    message: 'Address is too short',
                                },
                                maxLength: {
                                    value: 200,
                                    message: 'Address is too long',
                                },
                            }}
                            render={({ field }) => (
                                <FullWidthField
                                    {...field}
                                    label="Address"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    error={!!errors.address}
                                    helperText={errors.address?.message}
                                    InputLabelProps={{
                                        shrink: field.value !== '',
                                    }}
                                />
                            )}
                        />

                        <FormRow>
                            <Controller
                                name="city"
                                control={control}
                                rules={{ required: 'City is required' }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="City"
                                        fullWidth
                                        error={!!errors.city}
                                        helperText={errors.city?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="state"
                                control={control}
                                rules={{ required: 'State is required' }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="State"
                                        fullWidth
                                        error={!!errors.state}
                                        helperText={errors.state?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="zipCode"
                                control={control}
                                rules={{
                                    required: 'ZIP Code is required',
                                    pattern: {
                                        value: /^\d{5}(-\d{4})?$/,
                                        message: 'Enter a valid ZIP code',
                                    },
                                }}
                                render={({ field }) => (
                                    <FormField
                                        {...field}
                                        label="ZIP Code"
                                        fullWidth
                                        error={!!errors.zipCode}
                                        helperText={errors.zipCode?.message}
                                        InputLabelProps={{
                                            shrink: field.value !== '',
                                        }}
                                    />
                                )}
                            />
                        </FormRow>
                    </FormSection>

                    {/* File Upload Section */}
                    {/* <FormSection>
                        <SectionTitle>
                            <Typography variant="h6">Documents</Typography>
                        </SectionTitle>
                        <FormRow>
                            <Controller
                                name="mpa"
                                control={control}
                                rules={{ required: 'MPA file is required' }}
                                render={({ field }) => (
                                    <FormField
                                        label="MPA"
                                        type="file"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            accept: 'application/pdf,.pdf',
                                        }}
                                        disabled={fileUploading}
                                        helperText={
                                            watch('mpa')
                                                ? 'File uploaded: ' +
                                                watch('mpa')?.split('/').pop()
                                                : ''
                                        }
                                        onChange={async (
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const uploadedFilePath =
                                                    await uploadFileAndReturnPath(
                                                        file
                                                    );
                                                setValue(
                                                    'mpa',
                                                    uploadedFilePath
                                                );
                                            }
                                        }}
                                        error={!!errors.mpa}
                                    />
                                )}
                            />
                            <Controller
                                name="persona"
                                control={control}
                                rules={{ required: 'Persona file is required' }}
                                render={({ field }) => (
                                    <FormField
                                        label="Persona"
                                        type="file"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            accept: 'application/pdf,.pdf',
                                        }}
                                        disabled={fileUploading}
                                        onChange={async (
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const uploadedFilePath =
                                                    await uploadFileAndReturnPath(
                                                        file
                                                    );
                                                setValue(
                                                    'persona',
                                                    uploadedFilePath
                                                );
                                            }
                                        }}
                                        helperText={
                                            watch('persona')
                                                ? 'File uploaded: ' +
                                                watch('persona')
                                                    ?.split('/')
                                                    .pop()
                                                : ''
                                        }
                                        error={!!errors.persona}
                                    />
                                )}
                            />
                        </FormRow>
                    </FormSection> */}

                    {/* Action Buttons */}
                    <ButtonContainer>
                        <ResetButton
                            variant="outlined"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            Reset
                        </ResetButton>
                        <ResetButton
                            variant="outlined"
                            onClick={() => navigate('/merchants')}
                            disabled={loading}
                        >
                            Cancel
                        </ResetButton>
                        <SubmitButton
                            type="submit"
                            variant="contained"
                            disabled={loading || fileUploading}
                        >
                            {loading || fileUploading ? (
                                <CircularProgress size={24} />
                            ) : id ? (
                                'Update'
                            ) : (
                                'Submit'
                            )}
                        </SubmitButton>
                    </ButtonContainer>
                </form>

                {/* Snackbar messages */}
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error">
                        {error}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!success}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert onClose={handleCloseSnackbar} severity="success">
                        {success}
                    </Alert>
                </Snackbar>
            </FormContainer>
        </LocalizationProvider>
    );
};

export default MerchantForm;
